<script setup lang="ts">
  import moment from "moment";
  import { Badge } from "@/components/ui/badge";
  import { Button } from "@/components/ui/button";
  import AiStar from "@/components/episto-ui/ai/AiStar.vue";
  import type { AdTemplateVersion, AdTemplateVersionStatus } from "@/types";

  defineProps<{
    versions: AdTemplateVersion[];
    openedVersionId: number | null;
    locked: boolean;
  }>();

  const emit = defineEmits<{
    open: [version: AdTemplateVersion];
    publish: [version: AdTemplateVersion];
  }>();

  const STATUS_BADGES: Record<AdTemplateVersionStatus, { label: string; variant: "success" | "info" | "secondary" }> = {
    published: { label: "Published", variant: "success" },
    draft: { label: "Draft", variant: "info" },
    archived: { label: "Archived", variant: "secondary" },
  };
</script>

<template>
  <p
    v-if="locked"
    class="pb-2 text-xs text-status-warning-strong">
    Save or discard your changes before opening or publishing another version.
  </p>
  <ul class="flex flex-col divide-y divide-border">
    <li
      v-for="version in versions"
      :key="version.id"
      class="flex items-start justify-between gap-4 py-3"
      :class="{ 'bg-muted/50': version.id === openedVersionId }">
      <div class="flex min-w-0 flex-col gap-1">
        <div class="flex items-center gap-2">
          <span class="heading-section">v{{ version.number }}</span>
          <Badge :variant="STATUS_BADGES[version.status].variant">
            {{ STATUS_BADGES[version.status].label }}
          </Badge>
          <span
            v-if="version.source === 'ai'"
            class="flex items-center gap-1 text-xs text-muted-foreground">
            <AiStar class="size-3.5" />
            Episto AI
          </span>
        </div>
        <p class="text-sm">{{ version.message || "No message" }}</p>
        <p class="text-xs text-muted-foreground">
          {{ version.author?.name || "Unknown author" }} · {{ moment(version.created_at).format("D MMM YYYY HH:mm") }}
        </p>
      </div>
      <div class="flex shrink-0 gap-2">
        <Button
          size="sm"
          variant="outline"
          :disabled="locked || version.id === openedVersionId"
          @click="emit('open', version)">
          Open
        </Button>
        <Button
          v-if="version.status !== 'published'"
          size="sm"
          :disabled="locked"
          @click="emit('publish', version)">
          Publish
        </Button>
      </div>
    </li>
  </ul>
</template>
